import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from "react-router-dom";
import App from './App.jsx';
import './index.css';
import { ErrorBoundary } from "react-error-boundary";
import GlobalErrorFallback from "./components/GlobalErrorFallback";
import appConfig from "./config";

// Инициализация хранилища логов
window.__APP_LOGS__ = [];
window.__APP_LOG_QUEUE__ = [];
window.__APP_LOG_FLUSH_INTERVAL__ = 5000;
window.__APP_LOG_BATCH_SIZE__ = 10;

// Функция отправки логов на бэкенд
const sendLogsToBackend = async (logs) => {
    if (!logs || logs.length === 0) return;

    try {
        const response = await fetch(`${appConfig.endpoint}/logs/create`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ logs })
        });

        if (!response.ok) {
            console.error('Failed to send logs:', response.status);
        }
    } catch (error) {
        console.error('Error sending logs:', error);
    }
};

// Функция для добавления лога в очередь
const queueLog = (log) => {
    window.__APP_LOG_QUEUE__.push(log);

    if (window.__APP_LOG_QUEUE__.length >= window.__APP_LOG_BATCH_SIZE__) {
        flushLogs();
    }
};

// Функция для отправки накопленных логов
const flushLogs = () => {
    if (window.__APP_LOG_QUEUE__.length === 0) return;

    const logsToSend = [...window.__APP_LOG_QUEUE__];
    window.__APP_LOG_QUEUE__ = [];

    sendLogsToBackend(logsToSend);
};

// Периодическая отправка логов
setInterval(flushLogs, window.__APP_LOG_FLUSH_INTERVAL__);

// Сохраняем оригинальные методы
const originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info
};

// Функция для определения типа действия
const getActionType = (args, type) => {
    const firstArg = args[0];
    if (typeof firstArg === 'string') {
        // Пытаемся определить тип действия по тексту
        if (firstArg.includes('render') || firstArg.includes('mount')) return 'render';
        if (firstArg.includes('click') || firstArg.includes('press')) return 'user_action';
        if (firstArg.includes('fetch') || firstArg.includes('request')) return 'api';
        if (firstArg.includes('error') || firstArg.includes('fail')) return 'error';
        if (firstArg.includes('success') || firstArg.includes('complete')) return 'success';
        if (firstArg.includes('payment') || firstArg.includes('checkout')) return 'payment';
        if (firstArg.includes('auth') || firstArg.includes('login')) return 'auth';
        if (firstArg.includes('data')) return 'data';
    }
    return type; // fallback к типу console (log, error и т.д.)
};

// Функция для извлечения URL или API эндпоинта
const extractEndpoint = (args) => {
    if (args.length > 0) {
        const msg = String(args[0]);
        const urlMatch = msg.match(/(https?:\/\/[^\s]+)/);
        if (urlMatch) return urlMatch[0];

        const apiMatch = msg.match(/(\/api\/[^\s]+)/);
        if (apiMatch) return apiMatch[0];
    }
    return null;
};

// Перехватываем все console методы
["log", "warn", "error", "info"].forEach((type) => {
    console[type] = (...args) => {
        // Формируем сообщение
        const message = args.map(a => {
            if (a instanceof Error) {
                return `${a.message}\n${a.stack}`;
            }
            if (typeof a === 'object') {
                try {
                    return JSON.stringify(a, null, 2);
                } catch {
                    return String(a);
                }
            }
            return String(a);
        }).join(" ");

        // Локальный лог для отображения
        const localLog = {
            type,
            message,
            time: Date.now(),
        };
        window.__APP_LOGS__.push(localLog);

        // Определяем тип действия и эндпоинт
        const actionType = getActionType(args, type);
        const endpoint = extractEndpoint(args);

        // Формируем имя для поля name (макс 45 символов)
        let logName = '';
        if (endpoint) {
            logName = `api.${actionType}`;
        } else if (args[0] && typeof args[0] === 'string') {
            // Берем первые 30 символов сообщения и добавляем тип
            const shortMsg = args[0].substring(0, 30).replace(/\s+/g, '_').toLowerCase();
            logName = `${actionType}.${shortMsg}`;
        } else {
            logName = `console.${actionType}`;
        }

        // Обрезаем до 45 символов
        logName = logName.substring(0, 45);

        // Подготовка лога для отправки на бэкенд
        const backendLog = {
            category: `client_app.console.${type}`,
            level: type === 'error' ? 'error' : type === 'warn' ? 'warning' : 'info',
            name: logName,
            details: {
                message,
                full_message: message.substring(0, 1000), // ограничим размер
                action: actionType,
                endpoint: endpoint,
                args: args.map(a =>
                    a instanceof Error ? { message: a.message, stack: a.stack } :
                        typeof a === 'object' ? JSON.stringify(a).substring(0, 500) : String(a).substring(0, 500)
                ),
                url: window.location.href,
                userAgent: navigator.userAgent,
                timestamp: Date.now(),
                restaurantId: window.__APP_CONTEXT__?.restaurantId,
                userId: window.__APP_CONTEXT__?.userId
            },
            createdAt: Math.floor(Date.now() / 1000)
        };

        queueLog(backendLog);
        originalConsole[type](...args);
    };
});

// Перехватываем глобальные ошибки
window.addEventListener('error', (event) => {
    const errorLog = {
        category: 'client_app.global.error',
        level: 'error',
        name: `global.${event.filename?.split('/').pop() || 'unknown'}`.substring(0, 45),
        details: {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            error: event.error?.stack || String(event.error),
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: Date.now()
        },
        createdAt: Math.floor(Date.now() / 1000)
    };

    window.__APP_LOG_QUEUE__.push(errorLog);

    window.__APP_LOGS__.push({
        type: 'error',
        message: `Global error: ${event.message}`,
        time: Date.now()
    });
});

// Перехватываем необработанные промисы
window.addEventListener('unhandledrejection', (event) => {
    const errorLog = {
        category: 'client_app.unhandled.rejection',
        level: 'error',
        name: 'unhandled.promise',
        details: {
            reason: event.reason?.message || String(event.reason),
            stack: event.reason?.stack,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: Date.now()
        },
        createdAt: Math.floor(Date.now() / 1000)
    };

    window.__APP_LOG_QUEUE__.push(errorLog);

    window.__APP_LOGS__.push({
        type: 'error',
        message: `Unhandled rejection: ${event.reason?.message || String(event.reason)}`,
        time: Date.now()
    });
});

// Экспортируем функцию для ручной отправки логов
window.__sendCustomLog = (action, data = {}, level = 'info') => {
    const customLog = {
        category: `client_app.custom.${action}`,
        level: level,
        name: `custom.${action}`.substring(0, 45),
        details: {
            action,
            data,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: Date.now(),
            restaurantId: window.__APP_CONTEXT__?.restaurantId,
            userId: window.__APP_CONTEXT__?.userId
        },
        createdAt: Math.floor(Date.now() / 1000)
    };

    window.__APP_LOG_QUEUE__.push(customLog);

    window.__APP_LOGS__.push({
        type: level,
        message: `[CUSTOM] ${action}: ${JSON.stringify(data)}`,
        time: Date.now()
    });
};

// Функция для установки контекста
window.__setLogContext = (context) => {
    window.__APP_CONTEXT__ = {
        ...window.__APP_CONTEXT__,
        ...context
    };
};

// Отправляем логи при закрытии страницы
window.addEventListener('beforeunload', () => {
    if (window.__APP_LOG_QUEUE__.length > 0) {
        const logs = [...window.__APP_LOG_QUEUE__];
        const blob = new Blob([JSON.stringify({ logs })], { type: 'application/json' });
        navigator.sendBeacon(`${appConfig.endpoint}/logs/create`, blob);
    }
});

// --- Глобальный ErrorBoundary ---
ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
        <ErrorBoundary
            FallbackComponent={GlobalErrorFallback}
            onError={(error, info) => {
                originalConsole.error("Global React error:", error, info);

                const errorLog = {
                    category: 'client_app.react.error',
                    level: 'error',
                    name: 'react.error.boundary',
                    details: {
                        error: error.message,
                        stack: error.stack,
                        componentStack: info.componentStack,
                        url: window.location.href,
                        userAgent: navigator.userAgent,
                        timestamp: Date.now()
                    },
                    createdAt: Math.floor(Date.now() / 1000)
                };

                window.__APP_LOG_QUEUE__.push(errorLog);

                window.__APP_LOGS__.push({
                    type: 'error',
                    message: `React error: ${error.message}`,
                    time: Date.now()
                });
            }}
        >
            <BrowserRouter basename={window.APP_CONFIG?.basePath || '/app'}>
                <App />
            </BrowserRouter>
        </ErrorBoundary>
    </React.StrictMode>
);